import React, { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Megaphone, PlusCircle, Loader2, X } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * Per-category sponsored banner carousel.
 *
 * Data: GET /api/classifieds/banners?country=NZ&category=<slug>&placement=category_top
 *
 * Iter-284 rev: the slot is now a rotating carousel — the shared backend
 * returns all active banners sorted by priority desc; we render every one
 * of them with auto-advance (6s) + arrow + dot controls, matching the
 * home-hero rotation feel.
 *
 * Empty API → per-slug LOCAL_FALLBACKS[slug] → GLOBAL_FALLBACK
 * (`Advertise your business here` with a mailto enquiry). Every rendered
 * frame also carries a small "+ Advertise here" chip so prospective
 * sponsors can enquire from any category page.
 */

const ROTATE_MS = 6000;

const LOCAL_FALLBACKS = {
  cars: {
    image_url: "https://images.unsplash.com/photo-1517940310602-26535839fe84?auto=format&fit=crop&w=1600&q=70",
    headline: "Sell your car in 60 seconds",
    subline: "Post free, add photos, chat with buyers across NZ.",
    cta_label: "Post a car ad",
    cta_href: "/post-ad",
    sponsor_name: "Allsale Auto",
    accent: "from-slate-900 via-[#0F70A8] to-[#1CA9E0]",
  },
  property: {
    image_url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=70",
    headline: "List your rental or property",
    subline: "Reach thousands of Kiwi renters and buyers — free to post.",
    cta_label: "List a property",
    cta_href: "/post-ad",
    sponsor_name: "Allsale Property",
    accent: "from-emerald-900 via-slate-900 to-[#0F70A8]",
  },
  mobile: {
    image_url: "https://images.unsplash.com/photo-1512446733611-9099a758e63c?auto=format&fit=crop&w=1600&q=70",
    headline: "Trade your phone the smart way",
    subline: "Verified buyers, secure reveal, WhatsApp built-in.",
    cta_label: "Sell your phone",
    cta_href: "/post-ad",
    sponsor_name: "Allsale Mobile",
    accent: "from-slate-900 via-[#1CA9E0] to-[#0F70A8]",
  },
  jobs: {
    image_url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=70",
    headline: "Hire in Aotearoa, faster",
    subline: "Post a job — reach active local talent within minutes.",
    cta_label: "Post a job",
    cta_href: "/post-ad",
    sponsor_name: "Allsale Jobs",
    accent: "from-[#1CA9E0] via-slate-900 to-slate-900",
  },
  bikes: {
    image_url: "https://images.unsplash.com/photo-1502744688674-c619d1586c9e?auto=format&fit=crop&w=1600&q=70",
    headline: "Cycle, e-bike or motorbike?",
    subline: "Find your next ride — or list yours in a click.",
    cta_label: "Browse bikes",
    cta_href: "/c/bikes",
    sponsor_name: "Allsale",
    accent: "from-slate-900 via-[#0F70A8] to-emerald-900",
  },
  "electronics-appliances": {
    image_url: "https://images.unsplash.com/photo-1587202372775-e229f172b9d7?auto=format&fit=crop&w=1600&q=70",
    headline: "Upgrade your home tech",
    subline: "TVs, appliances, computers — great deals near you.",
    cta_label: "Shop electronics",
    cta_href: "/c/electronics-appliances",
    sponsor_name: "Allsale",
    accent: "from-slate-900 via-[#1CA9E0] to-slate-900",
  },
  "commercial-vehicles-spares-business": {
    image_url: "https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=1600&q=70",
    headline: "Grow your business",
    subline: "Commercial vehicles, parts, business opportunities across NZ.",
    cta_label: "Explore",
    cta_href: "/c/commercial-vehicles-spares-business",
    sponsor_name: "Allsale Business",
    accent: "from-slate-900 via-slate-900 to-[#F7941E]",
  },
  "home-decor": {
    image_url: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1600&q=70",
    headline: "Style your space",
    subline: "Furniture, décor and home essentials from Kiwi sellers.",
    cta_label: "Shop home",
    cta_href: "/c/home-decor",
    sponsor_name: "Allsale Home",
    accent: "from-[#0F70A8] via-slate-900 to-slate-900",
  },
  fashion: {
    image_url: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1600&q=70",
    headline: "Refresh your wardrobe",
    subline: "Preloved and new — men, women, kids across Aotearoa.",
    cta_label: "Shop fashion",
    cta_href: "/c/fashion",
    sponsor_name: "Allsale Fashion",
    accent: "from-pink-900 via-slate-900 to-[#0F70A8]",
  },
  "books-sports-hobbies-supplies": {
    image_url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1600&q=70",
    headline: "For every hobby",
    subline: "Books, gear, instruments, art — start something new.",
    cta_label: "Explore hobbies",
    cta_href: "/c/books-sports-hobbies-supplies",
    sponsor_name: "Allsale",
    accent: "from-slate-900 via-emerald-900 to-[#0F70A8]",
  },
  pets: {
    image_url: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?auto=format&fit=crop&w=1600&q=70",
    headline: "Find your new best mate",
    subline: "Pets and pet supplies from trusted Kiwi sellers.",
    cta_label: "Browse pets",
    cta_href: "/c/pets",
    sponsor_name: "Allsale Pets",
    accent: "from-amber-900 via-slate-900 to-[#0F70A8]",
  },
  service: {
    image_url: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=70",
    headline: "Local services, done right",
    subline: "Trades, tutors, cleaners — find or offer a service near you.",
    cta_label: "Post a service",
    cta_href: "/post-ad",
    sponsor_name: "Allsale Services",
    accent: "from-slate-900 via-[#0F70A8] to-slate-900",
  },
};

const GLOBAL_FALLBACK = {
  id: "global-fallback",
  image_url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=70",
  headline: "Advertise your business here",
  subline: "Reach thousands of Kiwis every day — talk to our sales team.",
  cta_label: "Become a sponsor",
  // No cta_href — the "Become a sponsor" click opens the enquiry drawer
  // so the founder gets a proper contact record instead of an untracked
  // mailto. Handled in the render.
  sponsor_name: "Allsale",
  accent: "from-slate-900 via-slate-900 to-[#0F70A8]",
  is_enquiry: true,
};

function useCategoryBanners(categorySlug) {
  return useQuery({
    queryKey: ["banners", "category_top", categorySlug],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/banners", {
        params: { country: "NZ", category: categorySlug, placement: "category_top" },
      });
      return data?.banners || [];
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

function sponsorHref(b) {
  if (b?.seller_slug) return `/seller/${b.seller_slug}`;
  if (b?.sponsor_href) return b.sponsor_href;
  return null;
}

export default function CategoryBanner({ categorySlug }) {
  const [idx, setIdx] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const { data: apiBanners = [], isLoading } = useCategoryBanners(categorySlug);

  // Compose the render list: real banners > local slug fallback > global.
  // We only fall back when the API returned an empty list — a live banner
  // set is the source of truth once one is booked.
  const banners = apiBanners.length
    ? apiBanners
    : [LOCAL_FALLBACKS[categorySlug] || GLOBAL_FALLBACK];

  const total = banners.length;
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

  useEffect(() => { setIdx(0); }, [total, categorySlug]);
  useEffect(() => {
    if (total < 2) return undefined;
    const t = setInterval(next, ROTATE_MS);
    return () => clearInterval(t);
  }, [next, total]);

  if (isLoading) {
    return <div className="skeleton w-full aspect-[16/9] sm:aspect-[24/7] md:aspect-[32/7] rounded-2xl" data-testid={`category-banner-${categorySlug}-loading`} />;
  }

  return (
    <div
      className="relative overflow-hidden rounded-2xl shadow-card"
      data-testid={`category-banner-${categorySlug}`}
      data-hero-source={apiBanners.length ? "api" : "fallback"}
    >
      <div className="relative aspect-[16/9] sm:aspect-[24/7] md:aspect-[32/7] w-full bg-slate-900">
        {banners.map((b, i) => {
          const active = i === idx;
          const isExternalCta = b.cta_href?.startsWith("http") || b.cta_href?.startsWith("mailto");
          const accent = b.accent || "from-slate-900 via-slate-900 to-[#0F70A8]";
          const sponsor = b.sponsor_name || "Allsale · Sponsored";
          const sponsorLabel = /sponsored/i.test(sponsor) ? sponsor : `Sponsored · ${sponsor}`;
          const sponsorLink = sponsorHref(b);
          return (
            <div
              key={b.id || `${categorySlug}-${i}`}
              className={`absolute inset-0 transition-opacity duration-700 ease-out ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              aria-hidden={!active}
              data-testid={`category-banner-slide-${categorySlug}-${i}`}
            >
              {b.image_url && (
                <img
                  src={b.image_url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  loading={i === 0 ? "eager" : "lazy"}
                />
              )}
              <div className={`absolute inset-0 bg-gradient-to-r ${accent} opacity-90`} />
              <div className="absolute inset-0 flex items-center px-6 md:px-14">
                <div className="max-w-lg text-white">
                  {sponsorLink ? (
                    <a
                      href={sponsorLink}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md px-2.5 py-1 border border-white/15 text-[10px] font-bold uppercase tracking-widest transition-colors"
                      data-testid={`category-banner-sponsor-${categorySlug}-${i}`}
                    >
                      <Megaphone className="w-3 h-3" /> {sponsorLabel}
                    </a>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md px-2.5 py-1 border border-white/15 text-[10px] font-bold uppercase tracking-widest">
                      <Megaphone className="w-3 h-3" /> {sponsorLabel}
                    </div>
                  )}
                  <h2 className="mt-2 md:mt-3 font-display text-xl sm:text-2xl md:text-4xl font-black leading-tight tracking-tight">
                    {b.headline}
                  </h2>
                  {b.subline && (
                    <p className="mt-1.5 text-white/85 text-sm md:text-base max-w-md leading-relaxed line-clamp-2">
                      {b.subline}
                    </p>
                  )}
                  {b.cta_label && (b.is_enquiry ? (
                    <button
                      type="button"
                      onClick={() => setEnquiryOpen(true)}
                      className="mt-3 md:mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold px-4 py-2 md:px-5 md:py-2.5 shadow-card transition-colors"
                      data-testid={`category-banner-cta-${categorySlug}-${i}`}
                    >
                      {b.cta_label}
                    </button>
                  ) : b.cta_href ? (
                    <a
                      href={b.cta_href}
                      target={isExternalCta ? "_blank" : undefined}
                      rel={isExternalCta ? "noreferrer" : undefined}
                      className="mt-3 md:mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold px-4 py-2 md:px-5 md:py-2.5 shadow-card transition-colors"
                      data-testid={`category-banner-cta-${categorySlug}-${i}`}
                    >
                      {b.cta_label}
                    </a>
                  ) : null)}
                </div>
              </div>
            </div>
          );
        })}

        {/* Rotation controls — only visible with 2+ slides */}
        {total > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous banner"
              data-testid={`category-banner-prev-${categorySlug}`}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/85 hover:bg-white text-slate-800 grid place-items-center shadow-card transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next banner"
              data-testid={`category-banner-next-${categorySlug}`}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/85 hover:bg-white text-slate-800 grid place-items-center shadow-card transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-3 md:bottom-5 flex items-center gap-1.5" data-testid={`category-banner-dots-${categorySlug}`}>
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to banner ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"}`}
                />
              ))}
            </div>
          </>
        )}

        {/* "+ Advertise here" enquiry chip — opens the drawer so we
             capture a real contact record via POST /classifieds/contact
             instead of an untracked mailto. */}
        <button
          type="button"
          onClick={() => setEnquiryOpen(true)}
          data-testid={`category-banner-enquire-${categorySlug}`}
          className="absolute top-3 right-3 md:top-4 md:right-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 hover:bg-white text-slate-800 text-[11px] font-bold px-2.5 py-1 shadow-sm ring-1 ring-slate-200 transition-colors"
        >
          <PlusCircle className="w-3 h-3" /> Advertise here
        </button>
      </div>
      {enquiryOpen && (
        <AdvertiseEnquiryDrawer
          categorySlug={categorySlug}
          onClose={() => setEnquiryOpen(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Advertise-here enquiry drawer                                */
/* ─────────────────────────────────────────────────────────── */

function AdvertiseEnquiryDrawer({ categorySlug, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const send = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/contact", {
        subject: "Category banner enquiry",
        category: categorySlug,
        placement: "category_top",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        message: form.message.trim() || `Please send pricing for a banner on /c/${categorySlug}.`,
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Thanks — our sales team will be in touch shortly.");
      onClose();
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send your enquiry — please try again.");
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can reply.");
      return;
    }
    send.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
      data-testid="advertise-enquiry-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6 md:p-7 space-y-4"
        data-testid="advertise-enquiry-drawer"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--brand-orange)]">
              Advertise on Allsale
            </div>
            <h3 className="mt-1 font-display text-xl font-black text-slate-900">
              Get pricing for the /c/{categorySlug} banner
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Tell us a bit about your business and we&apos;ll be in touch with rates + booking dates.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close enquiry"
            data-testid="advertise-enquiry-close"
            className="w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2 sm:col-span-1">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Name *</span>
            <input
              data-testid="advertise-enquiry-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="mt-1 w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
            />
          </label>
          <label className="block col-span-2 sm:col-span-1">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Email *</span>
            <input
              data-testid="advertise-enquiry-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="mt-1 w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
            />
          </label>
          <label className="block col-span-2">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Phone (optional)</span>
            <input
              data-testid="advertise-enquiry-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              inputMode="tel"
              className="mt-1 w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
            />
          </label>
          <label className="block col-span-2">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Message</span>
            <textarea
              data-testid="advertise-enquiry-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              placeholder={`I'd like to book a banner on /c/${categorySlug} — please send pricing and available slots.`}
              className="mt-1 w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={send.isPending}
          data-testid="advertise-enquiry-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
        >
          {send.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Send enquiry
        </button>
      </form>
    </div>
  );
}
